import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import {
  MenuList,
  MaterialIcon,
  MenuListItem,
  HOCs,
} from '@pearsontechnology/aero-components';

import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import BaseMoreButton from 'components/MoreButton';
import { ActionTypes } from 'constants/enums';
import { activityShape } from 'Workspace/types/activity';
import { dispatch } from 'pluginServices/store';
import meShape from 'Workspace/types/me';
import { featureKeys } from 'services/FeatureFlagService';

import messages from './messages';
import actionMessages from "services/ExportService/messages";
import {
  Menu,
  MenuListItemText,
  Spacer,
  SubMenu,
  MenuListItemWrapper,
} from './styles';

import { pushToast, incrementDownloadErrorReportsCount, decrementDownloadErrorReportsCount, updateToast, removeToast } from 'containers/ToastManager/actions';
import { status } from '../../../Publications/utils/constants';
import exportService from 'services/ExportService';
import enhanceWithFeatureFlagSubscription from 'hocs/enhanceWithFeatureFlagSubscription';
import { PLUGIN_TYPE_COURSE } from 'pluginServices/manifest/constants';
import { courseTypes, pluginList, checkReportOutputTypes } from './constant';
import { pushPreflightToast } from '../../../../utils/preFlightToast';
import activityService from 'services/ActivityService';
import { updateLanguages } from '../../../components/LanguageDialog/languageHelpers';
import { SOURCE_LANGUAGE_CODE } from 'constants/languageHelpers';


const { enhanceWithPopper } = HOCs;
const MoreButtonWithPopper = enhanceWithPopper(BaseMoreButton);

const ProjectActivityListActions = ({ activity, currentUser, onAction, getFeatureFlagValue }) => {
  const [actionsList, setActionsList] = useState([]);
  const [duplicateSubActionsList, setDuplicateSubActionsList] = useState([]);
  const [checkReportSubActionsList, setCheckReportSubActionsList] = useState([]);
  const [translateSubActionsList, setTranslateSubActionsList] = useState([]);
  const [sourceLanguage, setSourceLanguage] = useState(SOURCE_LANGUAGE_CODE); // Default source language
  const [isDuplicateSubMenuOpen, setIsDuplicateSubMenuOpen] = useState(false);
  const [isCheckReportSubMenuOpen, setIsCheckReportSubMenuOpen] = useState(false);
  const [isTranslateSubMenuOpen, setIsTranslateSubMenuOpen] = useState(false);
  const [activelyDownloadingReports, setActivelyDownloadingReports] = useState(new Set());
  const totalExportSnippets = useRef({});
  const snippet = useRef({});
  const {activityId} = activity;
  const {title} = activity.configFields;
  const isActivityDuplicateEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_ACTIVITY_DUPLICATE);
  const isWorkspaceCourseExportReport = getFeatureFlagValue(featureKeys.WORKSPACE_COURSE_EXPORT_REPORT);
  const isDuplicateWithSubOptionsEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_DUPLICATE_OPTIONS);
  const isPreflightPercentage = getFeatureFlagValue(featureKeys.BRNT_18784_WORKSPACE_ERROR_REPORT_PERCENTAGE_LOADER);
  const isTranslationsCoursePocEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_TRANSLATIONS_COURSE_POC);
    
  const onCompleteDownloadReportWithKey = (downloadKey) => () => {
    dispatch(removeToast({ toastId: downloadKey }));
  };
  
  const getDownloadKey = (activityId, outputType) => `${activityId}-${outputType}`;
  
  const unsubscribeExportAndResetSnippets = (exportId, outputType) => {
    exportService.unSubscribeToExport(exportId);
    const downloadKey = getDownloadKey(activityId, outputType);
    delete snippet.current[downloadKey];
    delete totalExportSnippets.current[downloadKey];
    setActivelyDownloadingReports((prev) => {
      const newSet = new Set(prev);
      newSet.delete(downloadKey);
      return newSet;
    });
  };

  const requestPreflightReport = async (outputType) => {
    const { exportElementsCount, exportId } = await exportService.requestPreflightReport(activityId, outputType);
    if (exportId) {
      const downloadKey = getDownloadKey(activityId, outputType);
      setActivelyDownloadingReports((prev) => new Set(prev).add(downloadKey));
      pushPreflightToast(
        actionMessages.generatingReport,
        title,
        downloadKey,
        exportId,
        () => unsubscribeExportAndResetSnippets( exportId, outputType )
      );
      totalExportSnippets.current[downloadKey] = exportElementsCount;
      snippet.current[downloadKey] = 0;
      exportService.subscribeToExport(exportId, (data) => receiveOnExportComplete(data, outputType));
    }
  };
  
  const getPublicationReport = async (outputType) => {
    exportService.exportForReport(activityId, title, (data) => receiveOnExportComplete(data, outputType), outputType);
  };

  const receiveOnExportComplete = (data, outputType) => {
    const { response } = data;
    const { exportId, progress, ambrosiaUrl } = response;
    const downloadKey = getDownloadKey(activityId, outputType);

    // Defensive guards
    const total = totalExportSnippets.current[downloadKey] || 0;
    snippet.current[downloadKey] = (snippet.current[downloadKey] || 0) + 1;

    // percentage math: avoid divide by zero and cap at 100
    const percentage = total > 0
      ? Math.min(100, Math.floor((snippet.current[downloadKey] / total) * 100))
      : 0;

    if (isPreflightPercentage) {
      const toast = {
        toastId: downloadKey,
        percentage: percentage,
      };
      dispatch(updateToast(toast));
    }

    const isCompleted = status[progress] === status.COMPLETED;

    // If the server reports completed but we haven't yet received all the snippets
    if (isCompleted) {
      if (total > 0 && snippet.current[downloadKey] < total) {
        if (isPreflightPercentage) {
          dispatch(updateToast({ toastId: activityId, percentage: 100 }));
        }
      }

      if (ambrosiaUrl) {
        unsubscribeExportAndResetSnippets(exportId, outputType);
        exportService.checkIfReportGenerated({ activityId, exportId, title, toastId: downloadKey }, onCompleteDownloadReportWithKey(downloadKey));
      }
    } else if (status[progress] === status.FAILED) {
      dispatch(decrementDownloadErrorReportsCount());
      unsubscribeExportAndResetSnippets(exportId, outputType);
      dispatch(pushToast({ message: intl.formatMessage(messages.reportFailed) }));
    }
  };

  const checkifReportDownloading = (type, outputType) => {
    if (type === ActionTypes.CHECK_REPORT && activityId && outputType) {
      const downloadKey = getDownloadKey(activityId, outputType);
      return activelyDownloadingReports.has(downloadKey);
    }
    return false;
  };
  
  const getCourseType = (plugin) => plugin.type === PLUGIN_TYPE_COURSE && pluginList[plugin.name] === courseTypes.ePub;
  const isPluginTypeEpub = getCourseType(activity.plugin);

  useEffect(() => {
    if (!isTranslationsCoursePocEnabled) {
      setTranslateSubActionsList([]);
      return;
    }

    let isMounted = true;

    const fetchLanguages = async () => {
      try {
        const response = await activityService.coursewareLanguagesGet(activityId);
        const responseLanguages = response?.languages || [];
        
        // Extract source language from response (if available) or default to SOURCE_LANGUAGE_CODE
        const sourceLang = response?.sourceLanguage || SOURCE_LANGUAGE_CODE;
        
        if (isMounted) {
          setSourceLanguage(sourceLang);
        }
        
        const updatedList = updateLanguages(responseLanguages);
        
        if (isMounted) {
          const _translateSubActionsList = updatedList.map((lang) => ({
            type: ActionTypes.TRANSLATE,
            message: lang.label,
            language: lang.value,
          }));
          setTranslateSubActionsList(_translateSubActionsList);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch languages for ProjectActivityListActions', error);
        const fallback = updateLanguages();
        if (isMounted) {
          setSourceLanguage(SOURCE_LANGUAGE_CODE); // Default to SOURCE_LANGUAGE_CODE on error
          const _translateSubActionsList = fallback.map((lang) => ({
            type: ActionTypes.TRANSLATE,
            message: lang.label,
            language: lang.value,
          }));
          setTranslateSubActionsList(_translateSubActionsList);
        }
      }
    };

    fetchLanguages();

    return () => {
      isMounted = false;
    };
  }, [activityId, isTranslationsCoursePocEnabled]);

  useEffect(() => {
    const _actionsList = [];
    const _duplicateSubActionsList = [];
    const _checkReportSubActionsList = [];

    if (translateSubActionsList.length > 0) {
      setIsTranslateSubMenuOpen(false);
      _actionsList.push({
        type: ActionTypes.TRANSLATE,
        icon: 'translate',
        message: messages.translate,
      });
    }
    if (isActivityDuplicateEnabled) {
      if (isDuplicateWithSubOptionsEnabled && isPluginTypeEpub) {
        setIsDuplicateSubMenuOpen(false);
        _duplicateSubActionsList.push({
          type: ActionTypes.DUPLICATE_EPUB,
          message: messages.ePub,
        });
        _duplicateSubActionsList.push({
          type: ActionTypes.DUPLICATE_BV,
          message: messages.bronteViewer,
        });
      }
      _actionsList.push({
        type: ActionTypes.DUPLICATE,
        icon: 'content_copy',
        message: messages.duplicate,
      });
    }
    if (activity.creator.accountId === currentUser.accountId) {
      _actionsList.push({
        type: ActionTypes.DELETE,
        icon: 'delete',
        message: messages.delete,
      });
    }
    if (isWorkspaceCourseExportReport) {
      setIsCheckReportSubMenuOpen(false);
      _checkReportSubActionsList.push({
        type: `${ActionTypes.CHECK_REPORT}-subAction1`,
        message: messages.pPlusInDesign,
        outputType: checkReportOutputTypes.P_PLUS_INDESIGN,
      });
      _checkReportSubActionsList.push({
        type: `${ActionTypes.CHECK_REPORT}-subAction2`,
        message: messages.channelPartners,
        outputType: checkReportOutputTypes.CHANNEL_PARTNER,
      });
      _actionsList.push({
        type: ActionTypes.CHECK_REPORT,
        icon: 'assignment_turned_in',
        message: messages.checkReport,
      });
    }
    setCheckReportSubActionsList(_checkReportSubActionsList);
    setDuplicateSubActionsList(_duplicateSubActionsList);
    setActionsList(_actionsList);
  }, [activity, currentUser, isActivityDuplicateEnabled, isWorkspaceCourseExportReport, translateSubActionsList, isTranslationsCoursePocEnabled]);

  if (!actionsList.length) {
    return <Spacer />;
  }

  const handleMenuClick = (type) => {
    if (type === ActionTypes.TRANSLATE && translateSubActionsList.length > 0) {
      setIsTranslateSubMenuOpen(!isTranslateSubMenuOpen);
      setIsDuplicateSubMenuOpen(false);
      setIsCheckReportSubMenuOpen(false);
    } else if (type === ActionTypes.DUPLICATE && isDuplicateWithSubOptionsEnabled && isPluginTypeEpub) {
      setIsDuplicateSubMenuOpen(!isDuplicateSubMenuOpen);
      setIsCheckReportSubMenuOpen(false);
      setIsTranslateSubMenuOpen(false);
    } else if (type === ActionTypes.CHECK_REPORT) {
      setIsCheckReportSubMenuOpen(!isCheckReportSubMenuOpen);
      setIsDuplicateSubMenuOpen(false);
      setIsTranslateSubMenuOpen(false);
    } else {
      onClickItem(type)();
    }
  };

  const onClickItem = (type, outputType, language) => () => {
    if (type === ActionTypes.CHECK_REPORT) {
      dispatch(incrementDownloadErrorReportsCount());
      if (isPreflightPercentage) {
        requestPreflightReport(outputType);
      } else {
        getPublicationReport(outputType);
      }
    }
    
    // Validate translation language - check if it matches source language
    if (type === ActionTypes.TRANSLATE && language) {
      const normalizedSelectedLang = language?.toLowerCase();
      const normalizedSourceLang = sourceLanguage?.toLowerCase();
      
      if (normalizedSelectedLang === normalizedSourceLang) {
        setIsDuplicateSubMenuOpen(false);
        setIsCheckReportSubMenuOpen(false);
        setIsTranslateSubMenuOpen(false);
        dispatch(pushToast({ 
          type: 'simple',
          message: intl.formatMessage(messages.sourceLanguageError),
        }));
        return;
      }
    }
    
    setIsDuplicateSubMenuOpen(false);
    setIsCheckReportSubMenuOpen(false);
    setIsTranslateSubMenuOpen(false);
    onAction(type, activity, language);

  };

  return (
    <MoreButtonWithPopper
      placement="bottom-end"
      hidepopperonclick={actionsList.every(
        (item) => item.type !== ActionTypes.DUPLICATE && isDuplicateWithSubOptionsEnabled && item.type !== ActionTypes.TRANSLATE
      )}
    >
      <Menu>
        <MenuList>
          {actionsList.map((item) => (
            <MenuListItem
              key={item.type}
              onClick={() => handleMenuClick(item.type)}
              data-type={`${item.type}`}
            >
              <MaterialIcon id={item.icon} />
              <MenuListItemText>
                {intl.formatMessage(item.message)}
              </MenuListItemText>
              {item.type === ActionTypes.TRANSLATE && translateSubActionsList.length > 0 && (
                <>
                  <MaterialIcon id={"chevron_right"} />
                  {isTranslateSubMenuOpen && (
                    <SubMenu>
                      <MenuList>
                        {translateSubActionsList.map((subItem) => (
                          <MenuListItem
                            key={subItem.language}
                            onClick={onClickItem(subItem.type, null, subItem.language)}
                          >
                            <MenuListItemText>{subItem.message}</MenuListItemText>
                          </MenuListItem>
                        ))}
                      </MenuList>
                    </SubMenu>
                  )}
                </>
              )}
              {item.type === ActionTypes.DUPLICATE && isDuplicateWithSubOptionsEnabled && isPluginTypeEpub && duplicateSubActionsList.length > 0 && (
                <>
                  <MaterialIcon id={"chevron_right"} />
                  {isDuplicateSubMenuOpen && (
                    <SubMenu>
                      <MenuList>
                        {duplicateSubActionsList.map((subItem) => (
                          <MenuListItem
                            key={subItem?.message?.id}
                            onClick={onClickItem(subItem.type)}
                          >
                            <MenuListItemText>{intl.formatMessage(subItem?.message)}</MenuListItemText>
                          </MenuListItem>
                        ))}
                      </MenuList>
                    </SubMenu>
                  )}
                </>
              )}
              {item.type === ActionTypes.CHECK_REPORT && checkReportSubActionsList.length > 0 && (
                <>
                  <MaterialIcon id={"chevron_right"} />
                  {isCheckReportSubMenuOpen && (
                    <SubMenu>
                      <MenuList>
                        {checkReportSubActionsList.map((subItem) => (
                          <MenuListItemWrapper key={subItem.type} activelyDownloadingReport={checkifReportDownloading(item.type, subItem.outputType)}>
                            <MenuListItem
                              key={subItem?.message?.id}
                              disabled={checkifReportDownloading(item.type, subItem.outputType)}
                              onClick={onClickItem(item.type, subItem.outputType)}
                              data-type={subItem?.type}
                            >
                              <MenuListItemText>{intl.formatMessage(subItem?.message)}</MenuListItemText>
                            </MenuListItem>
                          </MenuListItemWrapper>
                        ))}
                      </MenuList>
                    </SubMenu>
                  )}
                </>
              )}
            </MenuListItem>
          ))}
        </MenuList>
      </Menu>
    </MoreButtonWithPopper>
  );
};

ProjectActivityListActions.propTypes = {
  activity: activityShape,
  onAction: PropTypes.func,
  currentUser: meShape,
};

const flagNames = [featureKeys.WORKSPACE_ACTIVITY_DUPLICATE, featureKeys.WORKSPACE_COURSE_EXPORT_REPORT, featureKeys.BRNT_18784_WORKSPACE_ERROR_REPORT_PERCENTAGE_LOADER, featureKeys.WORKSPACE_TRANSLATIONS_COURSE_POC];
export default enhanceWithFeatureFlagSubscription(ProjectActivityListActions, flagNames);